import { useLocalization } from "@tm/localization"
import { Button, Badge } from "@tm/controls"
import { createPortal } from "react-dom"
import { Box, CircularProgress, Divider, Icon, styled, Tooltip, Typography, useMediaQuery } from "@tm/components"
import { useEffect, useState } from "react"
import { getBundleParams } from "../../utils"

type Props = {
    showApplyBtn?: boolean
    showSkipBtn?: boolean
    skipBtnText?: string
    disabledApplyBtn?: boolean
    loadingApplyBtn?: boolean
    showLoading?: boolean
    enableTyresTooltip?: boolean
    onApply?(): void
    onSkip?(): void
}

const StyledTooltip = styled(Tooltip)(({ theme }) => ({
    "& .MuiTooltip-tooltip, & .MuiTooltip-arrow::before": {
        borderColor: theme.palette.primary.main,
    },
    "& .MuiTooltip-tooltip": {
        maxWidth: "24rem",
    },
    zIndex: "1000",
}))

const StyledTooltipContent = styled(Box)({
    padding: "0 0.5rem",
    display: "flex",
    alignItems: "center",
    gap: "1rem",
})

const StyledBoxButton = styled(Box)({
    "@media (min-width: 1700px)": {
        height: "100%",
        display: "flex",
        alignItems: "center",
    },
})

function ApplyButton(props: Props) {
    const { disabledApplyBtn, onApply, onSkip, showApplyBtn, loadingApplyBtn, showSkipBtn, skipBtnText, showLoading, enableTyresTooltip } = props
    const { translateText } = useLocalization()
    const isLargeButton = useMediaQuery("(min-width: 1760px)")
    const isMediumButton = useMediaQuery("(min-width: 1640px)")

    const [el, setEl] = useState<HTMLElement | null>(null)
    const isLKQ = getBundleParams()?.version === "LKQ"

    useEffect(() => {
        const observer = new MutationObserver(() => {
            const element = document.getElementById("wheels-navigation")
            if (element) {
                setEl(element)
                observer.disconnect()
            }
        })

        observer.observe(document.body, { childList: true, subtree: true })

        return () => observer.disconnect()
    }, [])

    const getButtonClassName = () => {
        if (!isLKQ) {
            return ""
        }

        if (!showSkipBtn || (showApplyBtn && isLargeButton)) {
            return "applyBtn--large"
        }

        if (showSkipBtn && isMediumButton) {
            return "applyBtn--medium"
        }

        return ""
    }

    const renderTyresTooltip = () => {
        return (
            <StyledTooltipContent>
                <Icon name="bulb" />
                <Divider orientation="vertical" flexItem />
                <Typography>{translateText(14078)}</Typography>
            </StyledTooltipContent>
        )
    }

    const renderContent = () => {
        return (
            <div className="applyBtn--wrapper">
                {showLoading && disabledApplyBtn && <CircularProgress sx={{ marginRight: ".5em" }} size={25} />}
                {showSkipBtn && isLKQ && (
                    <Button size="m" className={getButtonClassName()} onClick={onSkip}>
                        {skipBtnText || translateText(1116)}
                    </Button>
                )}
                {showApplyBtn && (
                    <StyledTooltip
                        variant="light"
                        placement="bottom-start"
                        open={!!enableTyresTooltip && !!disabledApplyBtn}
                        title={renderTyresTooltip()}
                        PopperProps={{ container: el }}
                    >
                        <StyledBoxButton>
                            <Button
                                skin="success"
                                size="m"
                                className={`btn--apply ${getButtonClassName()}`}
                                disabled={disabledApplyBtn}
                                onClick={onApply}
                            >
                                {loadingApplyBtn && <Badge skin="dark" value={<CircularProgress size={10} />} />}
                                {translateText(22)}
                            </Button>
                        </StyledBoxButton>
                    </StyledTooltip>
                )}
                {showSkipBtn && !isLKQ && (
                    <Button size="m" onClick={onSkip}>
                        {skipBtnText || translateText(1116)}
                    </Button>
                )}
            </div>
        )
    }

    if (el) {
        return createPortal(renderContent(), el)
    }
    return null
}

export default ApplyButton
